import { Button } from '../components/ui/button'
import { Chip } from '../components/ui/chip'
import {
	GradientBackground,
	HideScrollbar,
} from '../components/ui/gradient-background'
import { StatusDot } from '../components/ui/status-dot'

interface LandingPageProps {
	/**
	 * Fires when the user clicks the primary CTA. The single in-app
	 * entry lands on `/home` (portfolio overview); the persistent
	 * left sidebar exposes the other 4 surfaces (Trade / Borrow /
	 * Earn / Settings).
	 */
	onEnter: () => void
}

/**
 * OctoGear landing — single hero, financial-grade dark.
 *
 * Composition:
 *   1. Live status chip (small, top)
 *   2. Wordmark with cyan→emerald gradient on "Gear" half
 *   3. One-line positioning + 3-line elaboration
 *   4. Three stat tiles — these are the trust signal
 *   5. Single primary CTA: Enter Pool Alpha → /home
 *   6. Provenance footer line
 *
 * The Trader / Curator / Operator role tiles that used to sit
 * below the CTA were removed in the nav rework — the in-app left
 * sidebar now exposes the surfaces (Home / Trade / Borrow / Earn /
 * Settings), and the landing focuses on getting the user into the
 * portfolio overview at /home.
 *
 * The hero uses Space Grotesk display face — sharper, more
 * technical than Inter. Numbers in stat tiles use JetBrains Mono.
 */
export function LandingPage({ onEnter }: LandingPageProps) {
	return (
		<div className="relative min-h-screen flex flex-col items-center justify-center px-5 py-12 text-center">
			<HideScrollbar />
			<GradientBackground />

			<div className="w-full max-w-2xl flex flex-col items-center">
				{/* Status chip */}
				<div className="mb-8">
					<Chip variant="intent">
						<StatusDot status="live" />
						Pool Alpha is live · Polygon mainnet
					</Chip>
				</div>

				{/* Brand wordmark */}
				<h1 className="font-display text-6xl md:text-[88px] font-bold tracking-[-0.04em] leading-[0.95] mb-6">
					Octo
					<span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-emerald-400 text-transparent bg-clip-text">
						Gear
					</span>
				</h1>

				{/* Tagline + elaboration */}
				<p className="text-lg md:text-xl text-foreground mb-3 font-medium">
					A prime brokerage credit layer for prediction markets.
				</p>
				<p className="text-base text-muted-foreground max-w-xl leading-relaxed mb-12">
					Earn delta-neutral yield on USDC, backed by overcollateralised credit
					accounts. Deposit from any chain via NEAR Intents — no bridging, no
					event-outcome exposure.
				</p>

				{/* Headline stat tiles */}
				<div className="grid grid-cols-3 gap-3 w-full mb-12">
					{[
						{ label: 'Apr 2026 PM volume', value: '$29.8B' },
						{ label: 'Target lender APY', value: '10–15%' },
						{ label: 'Cross-chain identity', value: 'Single ID' },
					].map((stat) => (
						<div
							key={stat.label}
							className="bg-card backdrop-blur-xl border border-border rounded-lg p-4 text-left hover:border-cyan-400/30 transition-colors"
						>
							<div className="font-mono font-medium tabular-nums text-lg leading-tight text-foreground">
								{stat.value}
							</div>
							<div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground mt-1.5">
								{stat.label}
							</div>
						</div>
					))}
				</div>

				{/* CTA */}
				<div className="flex flex-col gap-3 w-full max-w-sm">
					<Button variant="primary" size="lg" fullWidth onClick={onEnter}>
						Enter Pool Alpha →
					</Button>
				</div>

				{/* Provenance footer */}
				<div className="mt-12 flex flex-wrap justify-center gap-3 text-xs text-muted-foreground uppercase tracking-wider">
					<span>Whitepaper v3 · May 2026</span>
					<span aria-hidden="true">·</span>
					<span>Fork of Gearbox V3</span>
					<span aria-hidden="true">·</span>
					<span>NEAR UA orchestration</span>
				</div>
			</div>
		</div>
	)
}
